// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Checkbox, FormActions, Input, PricingFormulaEditor, Radio, Select, Skeleton, Toggle } from '@/components/ui'
import { useUnit, useUpdateUnit, useCreateUnit } from '@/hooks/useUnits'
import { useCreateMeter, useCreateReading } from '@/hooks/useMeters'
import { useRoomType, useRoomTypes } from '@/hooks/useRoomTypes'
import { useProperty } from '@/hooks/useProperties'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'
import type { PricingFormula, LateFeePolicy } from '@/types'
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning'
import { localToday } from '@/lib/date'

interface UnitFormData {
  name: string
  price: number
  area_sqm: number | null
  share_ratio: number | null
  /** Move the unit to a different room type within the same property. */
  room_type_id?: number
}

export function UnitFormPage() {
  const { t } = useTranslation('units')
  const { t: tc } = useTranslation('common')
  const { t: tp } = useTranslation('properties')
  const { t: tm } = useTranslation('meters')
  const { id, roomTypeId } = useParams<{ id?: string; roomTypeId?: string }>()
  const navigate = useNavigate()
  const translateError = useApiErrorTranslator('units')
  const addToast = useNotificationStore((s) => s.addToast)

  const isEditing = !!id
  const unitId = isEditing ? Number(id) : 0
  const roomTypeIdNum = isEditing ? 0 : Number(roomTypeId)

  // Edit-mode data fetching
  const { data: unit, isLoading } = useUnit(unitId)
  const { data: roomType } = useRoomType(isEditing ? (unit?.room_type_id || 0) : roomTypeIdNum)
  const { data: property } = useProperty(roomType?.property_id || 0)
  // All room types under the same property — used to populate the
  // "Move to room type" selector in edit mode.
  const { data: roomTypes } = useRoomTypes(roomType?.property_id || 0)
  const updateUnit = useUpdateUnit()
  const createUnit = useCreateUnit()
  const createMeter = useCreateMeter()
  const createReading = useCreateReading()
  const featureFlags = useFeatureFlags()
  const { data: accountSettings } = useAccountSettings()
  const showLateFees = featureFlags.lateFees && (accountSettings?.settings?.late_fees_enabled ?? false)
  const { timezone } = useLocalizationConfig()
  // Condominium accounts have no per-unit meters (403 meters_not_applicable_to_condominium) —
  // mirror the condo guard used for the area/share-ratio fields below.
  const isCondominium = accountSettings?.account_type === 'condominium'

  // Pricing formula override state
  const [overrideRates, setOverrideRates] = useState(false)
  const [pricingFormula, setPricingFormula] = useState<PricingFormula>([])

  // Late fee policy override state
  const defaultLateFeePolicy: LateFeePolicy = {
    enabled: false,
    type: 'fixed',
    amount: 0,
    grace_period_days: 0,
  }
  const [lateFeeOverride, setLateFeeOverride] = useState<'property_default' | 'override'>('property_default')
  const [lateFeePolicy, setLateFeePolicy] = useState<LateFeePolicy>(defaultLateFeePolicy)
  const [lateFeeSaveStatus, setLateFeeSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle')

  // Meters section state (create mode only)
  const [metersEnabled, setMetersEnabled] = useState(true)
  const [waterEnabled, setWaterEnabled] = useState(true)
  const [electricityEnabled, setElectricityEnabled] = useState(true)
  // Opening meter reading (the value currently visible on the physical meter).
  // Stored as previous_value of the first Reading so next month's reading
  // calculates consumption correctly. Serial is auto-generated below from the
  // unit name; the user can edit it later from the meter detail page.
  const [waterOpeningReading, setWaterOpeningReading] = useState('')
  const [electricityOpeningReading, setElectricityOpeningReading] = useState('')

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<UnitFormData>({
    defaultValues: isEditing ? undefined : {
      name: '',
      price: 0,
      area_sqm: null,
      share_ratio: null,
    },
  })

  // WEB-M3: warn before tab close/refresh with unsaved edits (beforeunload).
  useUnsavedChangesWarning(isDirty)

  useEffect(() => {
    if (isEditing && unit) {
      reset({
        name: unit.name,
        price: unit.price,
        area_sqm: unit.area_sqm ?? null,
        share_ratio: unit.share_ratio ?? null,
        room_type_id: unit.room_type_id,
      })
      const hasCustomFormula = unit.pricing_formula && unit.pricing_formula.length > 0
      setOverrideRates(!!hasCustomFormula)
      setPricingFormula(unit.pricing_formula ?? [])
      // Detect whether unit has its own late fee policy
      if (unit.late_fee_policy && Object.keys(unit.late_fee_policy).length > 0) {
        setLateFeeOverride('override')
        setLateFeePolicy(unit.late_fee_policy)
      } else {
        setLateFeeOverride('property_default')
        setLateFeePolicy(defaultLateFeePolicy)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, reset, isEditing])

  // Watch the price field so we can render a controlled input that strips
  // leading zeros on every keystroke (cosmetic display fix, BUG-6).
  const priceValue = watch('price')

  const handleOverrideToggle = (checked: boolean) => {
    setOverrideRates(checked)
    if (checked && pricingFormula.length === 0) {
      // Pre-populate with property's formula when first enabling override
      const propertyFormula = property?.pricing_formula
      if (propertyFormula && propertyFormula.length > 0) {
        setPricingFormula([...propertyFormula])
      }
    }
    if (!checked) {
      // Clear formula when disabling override (inherit from property)
      setPricingFormula([])
    }
  }

  const handleSaveLateFeePolicy = async () => {
    if (lateFeeOverride === 'override') {
      if (lateFeePolicy.enabled && lateFeePolicy.amount <= 0) {
        setLateFeeSaveStatus('error')
        return
      }
    }
    try {
      setLateFeeSaveStatus('saving')
      if (lateFeeOverride === 'override') {
        await updateUnit.mutateAsync({
          id: unitId,
          roomTypeId: unit!.room_type_id,
          late_fee_policy: lateFeePolicy,
        })
      }
      setLateFeeSaveStatus('success')
      setTimeout(() => setLateFeeSaveStatus('idle'), 3000)
    } catch (err) {
      setLateFeeSaveStatus('error')
      setTimeout(() => setLateFeeSaveStatus('idle'), 3000)
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleClearLateFeeOverride = async () => {
    try {
      await updateUnit.mutateAsync({
        id: unitId,
        roomTypeId: unit!.room_type_id,
        late_fee_policy: {},
      })
      setLateFeeOverride('property_default')
      setLateFeePolicy(defaultLateFeePolicy)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const onSubmit = async (data: UnitFormData) => {
    try {
    if (isEditing) {
      if (!unit) return
      // Only send room_type_id when the user actually changed it — sending
      // the unchanged value would still pass the controller's same-property
      // guard but is wasteful and would unnecessarily invalidate the same
      // list twice.
      const targetRoomTypeId =
        data.room_type_id && Number(data.room_type_id) !== unit.room_type_id
          ? Number(data.room_type_id)
          : undefined
      await updateUnit.mutateAsync({
        id: unitId,
        roomTypeId: unit.room_type_id,
        name: data.name,
        price: data.price,
        area_sqm: data.area_sqm ?? undefined,
        share_ratio: data.share_ratio ?? undefined,
        room_type_id: targetRoomTypeId,
        pricing_formula: overrideRates ? pricingFormula : [],
      })
      navigate(`/units/${unitId}`)
    } else {
      // Create mode
      const result = await createUnit.mutateAsync({
        roomTypeId: roomTypeIdNum,
        name: data.name,
        price: data.price,
      })
      const newUnitId = result.data.id

      // Fan-out meter creates with auto-generated serials, then create an
      // opening reading on each meter using the value the user entered.
      // Opening reading sets previous_value == current_value == entered value
      // so the meter is at zero consumption until next month's reading.
      // Condominium accounts have no per-unit meters — skip the fan-out
      // entirely so createMeter never fires the 403 meters_not_applicable_to_condominium.
      if (metersEnabled && !isCondominium) {
        const today = localToday(timezone)
        const meterTasks: Array<{
          type: 'water' | 'electricity'
          serial: string
          openingValue: number
        }> = []
        if (waterEnabled) {
          meterTasks.push({
            type: 'water',
            serial: `W-${data.name}`,
            openingValue: parseFloat(waterOpeningReading) || 0,
          })
        }
        if (electricityEnabled) {
          meterTasks.push({
            type: 'electricity',
            serial: `E-${data.name}`,
            openingValue: parseFloat(electricityOpeningReading) || 0,
          })
        }

        const meterResults = await Promise.allSettled(
          meterTasks.map(async (m) => {
            const meterResult = await createMeter.mutateAsync({
              unitId: newUnitId,
              meter_type: m.type,
              serial_number: m.serial,
            })
            await createReading.mutateAsync({
              meterId: meterResult.data.id,
              previous_value: m.openingValue,
              current_value: m.openingValue,
              read_at: today,
            })
            return meterResult
          })
        )

        meterResults.forEach((res, idx) => {
          if (res.status === 'rejected') {
            const meterType = meterTasks[idx].type
            addToast({
              type: 'warning',
              message: tm('messages.unit_created_meter_failed', {
                meter_type: meterType === 'water' ? tm('section.water') : tm('section.electricity'),
                error: translateError(res.reason),
              }),
            })
          }
        })
      }

      navigate(`/units/${newUnitId}`)
    }
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  // Edit mode loading state
  if (isEditing && isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card>
            <Skeleton className="h-32" />
          </Card>
        </div>
    )
  }

  if (isEditing && !unit) {
    return (
        <Card>
          <p className="text-center py-8">{t('empty.not_found')}</p>
        </Card>
    )
  }

  // Breadcrumbs differ between create and edit
  const breadcrumbItems = isEditing
    ? [
        { label: tc('nav.properties'), href: '/properties' },
        { label: `${t('title')} ${unit!.name}`, href: `/units/${unitId}` },
        { label: tc('actions.edit') },
      ]
    : [
        { label: tc('nav.properties'), href: '/properties' },
        {
          label: property?.name || roomType?.name || tc('nav.properties'),
          href: roomType ? `/room-types/${roomTypeIdNum}` : '/properties',
        },
        { label: t('form.title_new') },
      ]

  return (
      <div className="space-y-6">
        <Breadcrumbs items={breadcrumbItems} />

        <PageHeader
          title={isEditing ? `${t('edit_unit')} ${unit!.name}` : t('form.title_new')}
          description={isEditing ? t('edit_description') : undefined}
        />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Card title={t('unit_detail')}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label={t('labels.unit_name')}
                placeholder={t('placeholders.unit_name')}
                error={errors.name?.message}
                {...register('name', { required: t('validation.unit_name_required') })}
              />
              <Input
                label={t('labels.monthly_rent_thb')}
                type="number"
                placeholder={t('placeholders.monthly_rent')}
                error={errors.price?.message}
                // Spread register but override onChange so the displayed value
                // is always the parsed number — this strips transient leading
                // zeros (e.g. "05500" → 5500) on every keystroke. The value
                // prop makes the input controlled; persisted data is unchanged.
                {...register('price', {
                  required: t('validation.price_required'),
                  validate: (v) => (!isNaN(v) && v >= 0) || t('validation.must_be_positive'),
                })}
                value={isNaN(priceValue) || priceValue === undefined ? '' : priceValue}
                onChange={(e) => {
                  const parsed = e.target.value === '' ? NaN : parseFloat(e.target.value)
                  setValue('price', parsed, { shouldValidate: true, shouldDirty: true })
                }}
              />
              {/* Room type selector — edit mode only, and only when the
                  property has more than one room type to choose between.
                  Changes are restricted to the unit's current property by
                  the API; the Select naturally enforces that since we only
                  list this property's room types. */}
              {isEditing && (roomTypes?.length ?? 0) > 1 && (
                <Select
                  label={t('labels.room_type')}
                  hint={t('labels.room_type_move_hint')}
                  options={(roomTypes ?? []).map((rt) => ({
                    value: String(rt.id),
                    label: rt.name,
                  }))}
                  {...register('room_type_id', { valueAsNumber: true })}
                />
              )}
            </div>
          </Card>

          {accountSettings?.account_type === 'condominium' && (
            <Card title={t('labels.condominium_fields')}>
              <p className="text-sm text-base-content/60 mb-4">
                {t('labels.condominium_fields_description')}
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label={t('labels.area_sqm')}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  {...register('area_sqm', { valueAsNumber: true })}
                />
                <Input
                  label={t('labels.share_ratio')}
                  type="number"
                  step="0.0001"
                  placeholder="0.0000"
                  {...register('share_ratio', { valueAsNumber: true })}
                />
              </div>
            </Card>
          )}

          {isEditing && (
            <Card title={t('labels.utility_rates_override')}>
              <div className="mb-4">
                <Toggle
                  variant="primary"
                  label={t('labels.override_rates')}
                  checked={overrideRates}
                  onChange={(e) => handleOverrideToggle(e.target.checked)}
                />
              </div>

              {!overrideRates && (
                <p className="text-sm text-base-content/60">{t('labels.uses_property_default')}</p>
              )}

              {overrideRates && (
                <PricingFormulaEditor value={pricingFormula} onChange={setPricingFormula} />
              )}
            </Card>
          )}

          {isEditing && showLateFees && <Card title={tp('late_fee_policy.title')}>
            {/* Override selector */}
            <div className="flex gap-6 mb-4">
              <Radio
                name="late_fee_override"
                variant="primary"
                label={tp('late_fee_policy.use_property_default')}
                checked={lateFeeOverride === 'property_default'}
                onChange={() => setLateFeeOverride('property_default')}
              />
              <Radio
                name="late_fee_override"
                variant="primary"
                label={tp('late_fee_policy.unit_override')}
                checked={lateFeeOverride === 'override'}
                onChange={() => {
                  setLateFeeOverride('override')
                  // Pre-populate with property policy when first overriding
                  if (property?.late_fee_policy && Object.keys(property.late_fee_policy).length > 0) {
                    setLateFeePolicy(property.late_fee_policy)
                  }
                }}
              />
            </div>

            {/* Property default info */}
            {lateFeeOverride === 'property_default' && (
              <p className="text-sm text-base-content/60">
                {property?.late_fee_policy?.enabled
                  ? `${tp('late_fee_policy.fee_type_label')}: ${property.late_fee_policy.type === 'fixed' ? tp('late_fee_policy.fee_type_fixed') : tp('late_fee_policy.fee_type_percentage')} — ${property.late_fee_policy.amount}${property.late_fee_policy.type === 'percentage' ? '%' : '฿'}`
                  : tp('late_fee_policy.enable_label')}
              </p>
            )}

            {/* Override fields */}
            {lateFeeOverride === 'override' && (
              <>
                {/* Enable toggle */}
                <div className="mb-4">
                  <Toggle
                    variant="primary"
                    label={tp('late_fee_policy.enable_label')}
                    checked={lateFeePolicy.enabled}
                    onChange={(e) =>
                      setLateFeePolicy((prev) => ({ ...prev, enabled: e.target.checked }))
                    }
                  />
                </div>

                <div className={`space-y-4 ${!lateFeePolicy.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Fee Type */}
                    <div className="min-w-0">
                      <Select
                        label={tp('late_fee_policy.fee_type_label')}
                        value={lateFeePolicy.type}
                        onChange={(e) =>
                          setLateFeePolicy((prev) => ({
                            ...prev,
                            type: e.target.value as 'fixed' | 'percentage',
                          }))
                        }
                        disabled={!lateFeePolicy.enabled}
                        options={[
                          { value: 'fixed', label: tp('late_fee_policy.fee_type_fixed') },
                          { value: 'percentage', label: tp('late_fee_policy.fee_type_percentage') },
                        ]}
                        className="w-auto"
                      />
                    </div>

                    {/* Amount */}
                    <div className="form-control min-w-0">
                      <Input
                        label={tp('late_fee_policy.amount_label')}
                        type="number"
                        min={0}
                        step={lateFeePolicy.type === 'percentage' ? '0.01' : '1'}
                        value={lateFeePolicy.amount}
                        onChange={(e) =>
                          setLateFeePolicy((prev) => ({
                            ...prev,
                            amount: Number(e.target.value),
                          }))
                        }
                        disabled={!lateFeePolicy.enabled}
                        leftIcon={lateFeePolicy.type === 'fixed' ? <span className="text-base-content/60">฿</span> : undefined}
                        rightAddon={lateFeePolicy.type === 'percentage' ? <span className="text-base-content/60">%</span> : undefined}
                        hint={lateFeePolicy.type === 'percentage' ? tp('late_fee_policy.amount_hint_percentage') : undefined}
                      />
                    </div>
                  </div>

                  {/* Grace Period */}
                  <Input
                    label={tp('late_fee_policy.grace_period_label')}
                    type="number"
                    className="w-full md:w-48"
                    min={0}
                    step={1}
                    value={lateFeePolicy.grace_period_days}
                    onChange={(e) =>
                      setLateFeePolicy((prev) => ({
                        ...prev,
                        grace_period_days: Math.max(0, Math.floor(Number(e.target.value))),
                      }))
                    }
                    disabled={!lateFeePolicy.enabled}
                    hint={tp('late_fee_policy.grace_period_hint')}
                  />
                </div>

                {/* Save / Clear buttons */}
                <div className="flex items-center gap-3 mt-4 pt-4 border-t border-base-200 flex-wrap">
                  <Button
                    type="button"
                    onClick={handleSaveLateFeePolicy}
                    loading={lateFeeSaveStatus === 'saving'}
                  >
                    {tp('late_fee_policy.save_button')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClearLateFeeOverride}
                  >
                    {tp('late_fee_policy.clear_override')}
                  </Button>
                  {lateFeeSaveStatus === 'success' && (
                    <span className="text-sm text-success">{tp('late_fee_policy.save_success')}</span>
                  )}
                  {lateFeeSaveStatus === 'error' && (
                    <span className="text-sm text-error">{tp('late_fee_policy.save_error')}</span>
                  )}
                </div>
              </>
            )}
          </Card>}

          {/* Meters section — create mode only; condominium accounts have no
              per-unit meters (403 meters_not_applicable_to_condominium). */}
          {!isEditing && !isCondominium && (
            <Card title={tm('section.title')}>
              <div className="mb-4">
                <Toggle
                  variant="primary"
                  label={tm('section.toggle_label')}
                  checked={metersEnabled}
                  onChange={(e) => setMetersEnabled(e.target.checked)}
                />
              </div>

              {metersEnabled && (
                <div className="space-y-4">
                  {/* Water meter */}
                  <div className="flex items-start gap-4">
                    <Checkbox
                      variant="primary"
                      label={<span className="w-36 inline-block">{tm('section.water')}</span>}
                      checked={waterEnabled}
                      onChange={(e) => setWaterEnabled(e.target.checked)}
                    />
                    {waterEnabled && (
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        placeholder={tm('section.opening_reading_placeholder')}
                        value={waterOpeningReading}
                        onChange={(e) => setWaterOpeningReading(e.target.value)}
                        className="flex-1"
                        hint={tm('section.opening_reading_hint')}
                      />
                    )}
                  </div>

                  {/* Electricity meter */}
                  <div className="flex items-start gap-4">
                    <Checkbox
                      variant="primary"
                      label={<span className="w-36 inline-block">{tm('section.electricity')}</span>}
                      checked={electricityEnabled}
                      onChange={(e) => setElectricityEnabled(e.target.checked)}
                    />
                    {electricityEnabled && (
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        placeholder={tm('section.opening_reading_placeholder')}
                        value={electricityOpeningReading}
                        onChange={(e) => setElectricityOpeningReading(e.target.value)}
                        className="flex-1"
                        hint={tm('section.opening_reading_hint')}
                      />
                    )}
                  </div>
                </div>
              )}
            </Card>
          )}

          <FormActions>
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              {tc('actions.cancel')}
            </Button>
            <Button type="submit" loading={isSubmitting || createUnit.isPending}>
              {isEditing ? tc('actions.save_changes') : t('form.title_new')}
            </Button>
          </FormActions>
        </form>
      </div>
  )
}
